/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { debounce } from "lodash"; // npm install lodash
import { calendarSx, dateInputBoxSx, dateInputTextSx } from "assets/theme/commonCalendarSx";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Popover from "@mui/material/Popover";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material"; // for loading indicator

// API for leave types (only used in history mode)
import { getLeavePolicies } from "api/leave";
import { useAuth } from "AuthContext";
import { useMemo } from "react";

const UserFilters = ({
  filters,
  onFilterChange,
  searchTerm,
  onSearchChange,
  dropdownOptions,        // { branches, shifts, departments, designations, roles }
  loading,                 // { branches, shifts, departments, designations, roles }
  fetchBranches,
  fetchSubBranches,
  fetchShifts,
  fetchDepartments,
  fetchDesignations,
  fetchRoles,
  showHistoryFilters = false,
  pageContext,
  statusOptions,
  historyActions,
}) => {
  console.log('🟪 UserFilters rendered');   // ✅ LOG 2
  const { userPermission } = useAuth();


  // 🔽 YEH DO LINE ADD KARO 🔽
  // const prevBranchIdRef = useRef();
  // const fetchShiftsRef = useRef(fetchShifts);
  // const prevFetchShiftsRef = useRef(fetchShifts);

  // useEffect(() => {
  //   fetchShiftsRef.current = fetchShifts;
  //   prevFetchShiftsRef.current = fetchShifts;
  // }, [fetchShifts]);

  // ---------- Debounced fetch functions (100ms) ----------
  const debouncedFetchBranches = useRef(debounce(fetchBranches, 100)).current;
  const debouncedFetchDepartments = useRef(debounce(fetchDepartments, 100)).current;
  const debouncedFetchDesignations = useRef(debounce(fetchDesignations, 100)).current;
  const debouncedFetchRoles = useRef(debounce(fetchRoles, 100)).current;

  const permissionSet = useMemo(() => {
    if (!Array.isArray(userPermission)) return new Set();
    return new Set(userPermission);
  }, [userPermission]);

  const canShow = (filterName) => {
    if (!pageContext) return false;

    return permissionSet.has(
      `${pageContext}.${filterName}`
    );
  };

  // useEffect(() => {
  //   console.log('🟡 useEffect – branch_id:', filters.branch_id);

  //   // Prevent running if branch_id hasn't changed
  //   if (prevBranchIdRef.current === filters.branch_id) {
  //     console.log('⏭️ Skipping effect – branch_id same');
  //     return;
  //   }

  //   prevBranchIdRef.current = filters.branch_id;

  //   // Use the stable ref to call fetchShifts
  //   const currentFetchShifts = fetchShiftsRef.current;

  //   if (filters.branch_id) {
  //     currentFetchShifts(filters.subbranch_id);
  //   } else {
  //     currentFetchShifts(null);
  //   }
  // }, [filters.branch_id]);   // ✅ only branch_id in dependencies

  // ---------- Leave types (only for history) ----------
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([
    { value: "", label: "All Leave Types" },
  ]);

  useEffect(() => {
    if (!showHistoryFilters) return;

    const fetchLeaveTypes = async () => {
      try {
        const res = await getLeavePolicies();
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data || [];

        setLeaveTypeOptions([
          { value: "", label: "All Leave Types" },
          ...list.map((p) => ({
            value: String(p.id),
            label: `${p.leave_name} (${p.leave_code})`,
          })),
        ]);
      } catch (err) {
        console.error("Leave type load failed", err);
      }
    };

    fetchLeaveTypes();
  }, [showHistoryFilters]);

  // ---------- Date picker state ----------
  const [fromAnchor, setFromAnchor] = useState(null);
  const [toAnchor, setToAnchor] = useState(null);
  const resolvedStatusOptions = statusOptions || [
    { value: "", label: "All Status" },
    { value: "approved", label: "Approved" },
    { value: "pending", label: "Pending" },
    { value: "rejected", label: "Rejected" },
  ];

  // ---------- Helper to find selected option ----------
  const getSelected = (key, options) =>
    options.find((o) => String(o.value) === String(filters[key])) || options[0];

  // ---------- Custom select with loading spinner ----------
  const renderSelect = (
    label,
    valueKey,
    optionsArray,
    onChange,
    onFocusDebounced,
    isLoading,
    disabled = false,
    extraProps = {}
  ) => {
    const options = [
      { value: "", label: `All ${label}s` },
      ...optionsArray.map((item) => ({
        value: String(item.id || item.value || item),
        label: item.label || item.name || item,
      })),
    ];

    return (
      <SoftSelect
        label={label}
        value={getSelected(valueKey, options)}
        onChange={({ value }) => onChange(value)}
        onFocus={onFocusDebounced}
        options={options}
        isDisabled={disabled}
        {...extraProps}
      // Custom loading indicator inside the select (if your SoftSelect supports it)
      // If not, you can add a small spinner next to the select.
      />
    );
  };

  return (
    <div className="container-fluid">
      {/* ================= ROW 1 ================= */}
      <div className="row g-2 align-items-end">
        {/* BRANCH */}
        {canShow("BranchFilter") && (
          <div className="col-md">
            {renderSelect(
              "Branch",
              "branch_id",
              dropdownOptions.branches,
              (value) => {
                onFilterChange("branch_id", value);
                onFilterChange("subbranch_id", "");
                onFilterChange("shift_id", "");

                fetchSubBranches(value);
              },
              debouncedFetchBranches,
              loading.branches
            )}
            {loading.branches && (
              <CircularProgress size={16} sx={{ ml: 1, position: "absolute" }} />
            )}
          </div>
        )}

        {/* SUB BRANCH */}
        {canShow("SubBranchFilter") && (
          <div className="col-md">
            {renderSelect(
              "Sub Branch",
              "subbranch_id",
              dropdownOptions.subBranches,
              (value) => {
                onFilterChange("subbranch_id", value);
                onFilterChange("shift_id", "");

                fetchShifts(value);
              },
              null,
              loading.subBranches,
              !filters.branch_id // disable if no branch
            )}
            {loading.subBranches && (
              <CircularProgress size={16} sx={{ ml: 1, position: "absolute" }} />
            )}
          </div>
        )}

        {/* SHIFT */}
        {canShow("ShiftFilter") && (
          <div className="col-md">
            {renderSelect(
              "Shift",
              "shift_id",
              dropdownOptions.shifts,
              (value) => onFilterChange("shift_id", value),
              null, // no debounced fetch on focus (fetched via branch change)
              loading.shifts,
              !filters.subbranch_id // disabled if no branch selected
            )}
            {loading.shifts && (
              <CircularProgress size={16} sx={{ ml: 1, position: "absolute" }} />
            )}
          </div>
        )}

        {/* DEPARTMENT */}
        {canShow("DepartmentFilter") && (
          <div className="col-md">
            {renderSelect(
              "Department",
              "department_id",
              dropdownOptions.departments,
              (value) => onFilterChange("department_id", value),
              debouncedFetchDepartments,
              loading.departments
            )}
            {loading.departments && (
              <CircularProgress size={16} sx={{ ml: 1, position: "absolute" }} />
            )}
          </div>
        )}

        {/* DESIGNATION */}
        {canShow("DesignationFilter") && (
          <div className="col-md">
            {renderSelect(
              "Designation",
              "designation_id",
              dropdownOptions.designations,
              (value) => onFilterChange("designation_id", value),
              debouncedFetchDesignations,
              loading.designations
            )}
            {loading.designations && (
              <CircularProgress size={16} sx={{ ml: 1, position: "absolute" }} />
            )}
          </div>
        )}

        {/* ROLE */}
        {canShow("RoleFilter") && (
          <div className="col-md">
            {renderSelect(
              "Role",
              "role",
              dropdownOptions.roles.map((r) => ({ value: r.name, label: r.name })),
              (value) => onFilterChange("role", value),
              debouncedFetchRoles,
              loading.roles
            )}
            {loading.roles && (
              <CircularProgress size={16} sx={{ ml: 1, position: "absolute" }} />
            )}
          </div>
        )}

        {/* SEARCH */}
        {canShow("SearchFilter") && (
          <div className="col-md-2">
            <SoftInput
              label="Search"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* ================= ROW 2 (History filters) ================= */}
      {showHistoryFilters && (
        <div className="row g-2 align-items-end" style={{ marginTop: "0.08px" }}>
          {/* FROM DATE */}
          {canShow("FromDateFilter") && (
            <div className="col-md-2">
              <SoftBox sx={dateInputBoxSx} onClick={(e) => setFromAnchor(e.currentTarget)}>
                <CalendarTodayIcon fontSize="small" />
                <SoftTypography sx={{ ...dateInputTextSx, flexGrow: 1 }}>
                  {filters.from ? dayjs(filters.from).format("DD-MMM-YYYY") : "From Date"}
                </SoftTypography>
                {filters.from && (
                  <CloseIcon
                    fontSize="small"
                    sx={{ cursor: "pointer", color: "#9ca3af" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFilterChange("from", null);
                      setFromAnchor(null);
                    }}
                  />
                )}
              </SoftBox>
            </div>
          )}

          {/* TO DATE */}
          {canShow("ToDateFilter") && (
            <div className="col-md-2">
              <SoftBox sx={dateInputBoxSx} onClick={(e) => setToAnchor(e.currentTarget)}>
                <CalendarTodayIcon fontSize="small" />
                <SoftTypography sx={{ ...dateInputTextSx, flexGrow: 1 }}>
                  {filters.to ? dayjs(filters.to).format("DD-MMM-YYYY") : "To Date"}
                </SoftTypography>
                {filters.to && (
                  <CloseIcon
                    fontSize="small"
                    sx={{ cursor: "pointer", color: "#9ca3af" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFilterChange("to", null);
                      setToAnchor(null);
                    }}
                  />
                )}
              </SoftBox>
            </div>
          )}

          {/* STATUS */}
          {canShow("StatusFilter") && (
            <div className="col-md-2">
              <SoftSelect
                label="Status"
                value={
                  resolvedStatusOptions.find((option) => option.value === (filters.status || "")) ||
                  resolvedStatusOptions[0]
                }
                onChange={({ value }) => onFilterChange("status", value)}
                options={resolvedStatusOptions}
              />
            </div>
          )}

          {historyActions && (
            <div className="col-md-auto ms-auto">
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                {historyActions}
              </SoftBox>
            </div>
          )}
        </div>
      )}

      {/* FROM DATE CALENDAR POPOVER */}
      <Popover
        open={Boolean(fromAnchor)}
        anchorEl={fromAnchor}
        onClose={() => setFromAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            boxShadow: "0px 12px 30px rgba(0,0,0,0.25)",
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            sx={calendarSx}
            value={filters.from ? dayjs(filters.from) : null}
            onChange={(v) => {
              const date = v ? v.format("YYYY-MM-DD") : null;
              onFilterChange("from", date);
              setFromAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>

      {/* TO DATE CALENDAR POPOVER */}
      <Popover
        open={Boolean(toAnchor)}
        anchorEl={toAnchor}
        onClose={() => setToAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            boxShadow: "0px 12px 30px rgba(0,0,0,0.25)",
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            sx={calendarSx}
            value={filters.to ? dayjs(filters.to) : null}
            onChange={(v) => {
              const date = v ? v.format("YYYY-MM-DD") : null;
              onFilterChange("to", date);
              setToAnchor(null);
            }}
          />
        </LocalizationProvider>
      </Popover>
    </div>
  );
};

UserFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.shape({
    branches: PropTypes.array,
    subBranches: PropTypes.array,
    shifts: PropTypes.array,
    departments: PropTypes.array,
    designations: PropTypes.array,
    roles: PropTypes.array,
  }).isRequired,
  loading: PropTypes.shape({
    branches: PropTypes.bool,
    subBranches: PropTypes.bool,
    shifts: PropTypes.bool,
    departments: PropTypes.bool,
    designations: PropTypes.bool,
    roles: PropTypes.bool,
  }).isRequired,
  fetchBranches: PropTypes.func.isRequired,
  fetchSubBranches: PropTypes.func.isRequired,
  fetchShifts: PropTypes.func.isRequired,
  fetchDepartments: PropTypes.func.isRequired,
  fetchDesignations: PropTypes.func.isRequired,
  fetchRoles: PropTypes.func.isRequired,
  showHistoryFilters: PropTypes.bool,
  statusOptions: PropTypes.array,
  historyActions: PropTypes.node,
};

// export default UserFilters;

export default React.memo(UserFilters);
